import type { AboutContentInput } from '../domain/entities/AboutContent.entity';

/**
 * Real company content, provided directly by the client (Daruosh) on
 * 2026-08-09. Two sections from that source -- the specific mines/exploration
 * areas (تک‌اشخانه, باروق, ترخراب, اقبال) -- are intentionally NOT duplicated
 * here even though they were part of the same "About" document: they belong
 * in the Projects feature (features/projects/content/projects.content.ts),
 * which already has a purpose-built entity for exactly this data (location,
 * coordinates, minerals, status, dates). Populating that file requires a few
 * additional facts (approximate coordinates, start dates, cover images) that
 * were not yet provided -- inventing coordinates for a real company's public
 * map would be actively misleading, so that file still has its placeholder
 * content pending those specifics.
 *
 * `ceoMessage` below is a drafted stand-in built from the "نگاه ما به معدن"
 * passage of the source document, not a verbatim quote from مجید جعفری --
 * flagged here so it gets reviewed/personalized before publishing.
 */
export const aboutContent: AboutContentInput = {
  introduction:
    'شرکت داریوش معدن آذر در سال ۱۴۰۰ با هدف فعالیت حرفه‌ای، توسعه‌محور و بلندمدت در صنعت معدن و صنایع معدنی تأسیس شد. این مجموعه با تمرکز بر توسعه ذخایر معدنی، اکتشاف، استخراج، فرآوری و ایجاد ارزش افزوده در زنجیره معدن فعالیت می‌کند و تلاش دارد با ترکیب دانش فنی، سرمایه انسانی، مدیریت، فناوری و نگاه بلندمدت، پروژه‌های معدنی را از مرحله شناسایی و اکتشاف تا توسعه و بهره‌برداری پیش ببرد. نگاه شرکت صرفاً استخراج ماده معدنی نیست؛ بلکه هدف، ایجاد یک مجموعه معدنی توسعه‌پذیر است که بتواند در بخش‌های مختلف زنجیره ارزش معدن حضور داشته باشد.',
  story:
    'شرکت داریوش معدن آذر در سال ۱۴۰۰ تأسیس شد. از ابتدای شکل‌گیری، هدف مجموعه ایجاد یک ساختار تخصصی برای فعالیت در حوزه معدن و توسعه پروژه‌های معدنی بوده است. فعالیت شرکت در ادامه با تمرکز بر اکتشاف و شناسایی ذخایر، دریافت و توسعه محدوده‌های معدنی، استخراج و همچنین ایجاد زیرساخت برای ورود به حوزه فرآوری و صنایع معدنی گسترش پیدا کرده است. در حال حاضر، شرکت علاوه بر در اختیار داشتن پروانه بهره‌برداری یک معدن کلسیت سفید، دارای چند محدوده اکتشافی و همچنین مجوز تأسیس کارخانه ذوب سرب و روی است. این ساختار، مسیر شرکت را از یک فعالیت صرفاً معدنی به سمت توسعه یک زنجیره کامل‌تر از معدن تا فرآوری هدایت می‌کند.',
  mission:
    'مأموریت داریوش معدن آذر، شناسایی، توسعه و بهره‌برداری هوشمندانه از ظرفیت‌های معدنی و تبدیل منابع طبیعی به ارزش اقتصادی پایدار است. ما تلاش می‌کنیم با ترکیب دانش زمین‌شناسی و مهندسی معدن، مدیریت پروژه، سرمایه انسانی و فناوری، پروژه‌هایی را توسعه دهیم که علاوه بر توجیه اقتصادی، قابلیت رشد و توسعه در بلندمدت داشته باشند.',
  vision:
    'چشم‌انداز داریوش معدن آذر، تبدیل شدن به یک مجموعه معدنی و صنایع معدنی قدرتمند، نوآور و توسعه‌پذیر است. هدف ما ایجاد شرکتی نیست که تنها به یک معدن یا یک محصول وابسته باشد. می‌خواهیم مجموعه‌ای بسازیم که بتواند به‌صورت مستمر اکتشاف کند، ذخیره ایجاد کند، استخراج کند، فرآوری کند، ارزش افزوده ایجاد کند، وارد بازارهای بزرگ‌تر شود و دوباره توسعه پیدا کند؛ سیستمی معدنی در حال رشد که وابسته به حضور یک فرد نباشد و بتواند با توسعه ساختار، دانش، سرمایه انسانی و پروژه‌ها، به‌صورت مستقل به رشد خود ادامه دهد.',
  coreValues: ['نوآوری', 'عمل‌گرایی', 'یادگیری مستمر', 'شجاعت', 'صداقت', 'آینده‌نگری', 'خلق ارزش'],
  ceoMessage:
    'ما باور داریم که معدن فقط یک ذخیره زیر زمین نیست؛ یک فرصت برای ساختن است — فرصتی برای ایجاد صنعت، فناوری، اشتغال، دانش و ارزش اقتصادی. به همین دلیل تلاش می‌کنیم به هر پروژه معدنی از چند زاویه نگاه کنیم: زمین‌شناسی، مهندسی، اقتصاد، فناوری، بازار و آینده. ترکیب این نگاه‌هاست که می‌تواند یک ذخیره معدنی را از یک «ماده خام» به یک «دارایی اقتصادی قدرتمند» تبدیل کند.',
  ceoName: 'مجید جعفری — مدیرعامل',
  timeline: [
    {
      year: 1400,
      title: 'تأسیس شرکت',
      description:
        'داریوش معدن آذر با هدف فعالیت حرفه‌ای، توسعه‌محور و بلندمدت در صنعت معدن و صنایع معدنی تأسیس شد.'
    }
  ],
  statistics: [
    { label: 'سال تأسیس', value: '۱۴۰۰' },
    { label: 'معدن دارای پروانه بهره‌برداری', value: '۱' },
    { label: 'محدوده اکتشافی', value: '۳' },
    { label: 'مهندس و متخصص', value: '۳' }
  ],
  whyChooseUs: [
    'رویکرد توسعه‌محور و بلندمدت به‌جای فعالیت صرفاً استخراجی',
    'حضور هم‌زمان در چند حلقه از زنجیره ارزش معدن: اکتشاف، استخراج و فرآوری',
    'تیم تخصصی در حوزه‌های اکتشاف، زمین‌شناسی و استخراج معدن',
    'ساختار مدیریتی متمرکز و تصمیم‌گیری مستقیم مبتنی بر مالکیت'
  ],
  certifications: [],
  strategicGoals: [
    {
      title: 'اکتشاف و توسعه ذخایر معدنی',
      description: 'شناسایی، مطالعه و توسعه محدوده‌های معدنی با هدف کشف ذخایر اقتصادی و ایجاد پروژه‌های جدید.'
    },
    {
      title: 'استخراج',
      description:
        'توسعه و بهره‌برداری از ذخایر دارای مجوز و ایجاد زیرساخت‌های لازم برای استخراج اقتصادی و اصولی مواد معدنی.'
    },
    {
      title: 'فرآوری و صنایع معدنی',
      description: 'حرکت به سمت ایجاد ارزش افزوده برای مواد معدنی و توسعه زیرساخت‌های فرآوری.'
    },
    {
      title: 'توسعه پروژه‌های معدنی',
      description: 'شناسایی فرصت‌های جدید، ارزیابی اقتصادی و فنی پروژه‌ها و تبدیل ظرفیت‌های معدنی به پروژه‌های قابل توسعه.'
    }
  ],
  licenses: [
    {
      title: 'مجوز تأسیس کارخانه ذوب سرب و روی',
      description: 'محل: شهرک صنعتی شماره یک زاهدان.'
    },
    {
      title: 'پروانه بهره‌برداری معدن کلسیت سفید تک‌اشخانه',
      description: 'کلسیت سفید از مواد معدنی مورد استفاده در صنایع مختلف است.'
    },
    {
      title: 'محدوده‌های اکتشافی',
      description: 'باروق؛ ترخراب – شاهین‌دژ؛ اقبال – شاهین‌دژ.'
    }
  ],
  leadership: [
    { name: 'داریوش جعفری', role: 'صاحب امضای شرکت و سهامدار اصلی', ownershipPercent: 99 },
    { name: 'مجید جعفری', role: 'مدیرعامل', ownershipPercent: 1 }
  ],
  ctaLabel: 'تماس با ما',
  ctaHref: '/contact'
};
